import { toast } from 'react-toastify';
import { Modal, ModalContent, ModalFooter } from '@ds';
import { Form } from '@ui';
import { FormActions } from '@features/forms/FormActions/FormActions';
import { useCallback, useRef } from 'react';
import { useUpdateCaseMutation, useUpdateTxCenterORMutation } from '@/queries/case';
import { caseTxCenterExtendedSchema, CaseTxCenterFormType, txCenterFormValues } from './config';
import { EditCaseTxCenterForm } from './EditCaseTxCenterForm';
import { replaceEmptyWithNull } from '@/utils/helpers';
import { Case } from '@/api/apis/case';
import { retrieveUnifiedError } from '@/api/utils';
import { validateFormSubmit, prepareTxCenterSubmissionData } from './utils';

type EditCaseTxCenterModalProps = {
  isOpen: boolean;
  onClose: () => void;
  caseData: Case;
};

export const EditCaseTxCenterModal = ({ isOpen, onClose, caseData }: EditCaseTxCenterModalProps) => {
  const { mutateAsync: updateCaseMutation, isPending: updateCaseMutationPending } = useUpdateCaseMutation();
  const { mutateAsync: updateTxCenterORMutation, isPending: updateTxCenterORMutationPending } =
    useUpdateTxCenterORMutation();
  const initialCaseDataRef = useRef<Case>(caseData);
  const defaultValues = txCenterFormValues(initialCaseDataRef.current);

  const validateFormSubmitCallback = useCallback((data: unknown) => validateFormSubmit(data, caseData), [caseData]);

  const handleSubmit = async (formData: unknown) => {
    const { orFields, otherValuesToPatch } = prepareTxCenterSubmissionData(formData as CaseTxCenterFormType, caseData);
    const hasOrFields = Object.values(orFields).filter(Boolean).length > 0;
    const hasOtherFields = Object.keys(otherValuesToPatch).length > 0;

    try {
      if (hasOrFields) {
        await updateTxCenterORMutation({
          caseId: caseData.caseId,
          ...replaceEmptyWithNull(orFields),
        });
      }

      if (hasOtherFields) {
        await updateCaseMutation({
          caseId: caseData.caseId,
          ...replaceEmptyWithNull(otherValuesToPatch),
        });
      }

      if (hasOrFields || hasOtherFields) {
        toast.success('Case TX Center data successfully saved.');
      }
      onClose();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  return (
    <Modal
      classNames={{
        wrapper: 'flex-wrap',
        base: 'p-6 bg-base-80',
        header: 'flex justify-between p-0 pb-4 border-b-1 border-base-30',
        body: 'p-0 my-6',
        footer: 'p-0 pt-6 border-t-1 border-base-30 justify-between',
      }}
      size="4xl"
      backdrop="opaque"
      isOpen={isOpen}
      onClose={onClose}
      hideCloseButton
    >
      <ModalContent>
        <Form<CaseTxCenterFormType>
          defaultValues={defaultValues}
          schema={caseTxCenterExtendedSchema}
          onSubmit={handleSubmit}
        >
          <EditCaseTxCenterForm
            serviceType={initialCaseDataRef.current.serviceType}
            status={caseData.status}
            isExpeditedCase={caseData.isExpeditedCase}
            caseData={caseData}
          />
          <ModalFooter>
            <FormActions
              actions={{
                size: 'lg',
                discard: {
                  label: 'Discard',
                  variant: 'light',
                  handler: onClose,
                },
                submit: {
                  label: 'Save changes',
                  validateData: validateFormSubmitCallback,
                  isSubmitting: updateCaseMutationPending || updateTxCenterORMutationPending,
                  onSubmit: handleSubmit,
                },
              }}
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};
